import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { accountApiService, accountQueries } from '@/services/accountApi'
import { useAuth } from '@/contexts/AuthContext'

/**
 * Hook for signing in with Google
 */
export const useGoogleSignIn = () => {
  const queryClient = useQueryClient()
  const { refreshAuthState } = useAuth()

  return useMutation({
    mutationFn: ({ idToken, referredBy }: { idToken: string, referredBy?: string }) =>
      accountApiService.googleSignIn(idToken, referredBy),
    onSuccess: () => {
      refreshAuthState()
      queryClient.invalidateQueries({ queryKey: accountQueries.all })
    },
  })
}

/**
 * Hook for signing in with Apple
 */
export const useAppleSignIn = () => {
  const queryClient = useQueryClient()
  const { refreshAuthState } = useAuth()

  return useMutation({
    mutationFn: ({ idToken, referredBy }: { idToken: string, referredBy?: string }) =>
      accountApiService.appleSignIn(idToken, referredBy),
    onSuccess: () => {
      refreshAuthState()
      queryClient.invalidateQueries({ queryKey: accountQueries.all })
    },
  })
}

/**
 * Hook for signing out
 */
export const useSignOut = () => {
  const { logout } = useAuth()

  return useMutation({
    mutationFn: () => accountApiService.signOut(),
    onSuccess: () => {
      // Use auth context logout which clears credentials, cache, and navigates
      logout()
    },
  })
}

/**
 * Hook that returns true when the authenticated user has the admin role
 */
export const useIsAdmin = () => {
  const { data } = useQuery(accountQueries.me())
  return data?.account?.role === 'admin'
}

/**
 * Hook to update the account currency via PATCH /account/currency
 */
export const useUpdateCurrency = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (currency: string) => accountApiService.updateCurrency(currency),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueries.all })
    },
  })
}
